import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pill, Plus, ChevronLeft, Trash2, Check, Clock, XCircle, Pause, Edit2, History, Archive, RotateCcw, ChevronDown, ChevronUp, Camera } from "lucide-react";
import api from "../../lib/api";
import { Spinner } from "../common";
import MedicationDialog from "./MedicationDialog";
import AttachmentsPanel from "./AttachmentsPanel";

const STATUS_LABEL = { taken: "Taken", skipped: "Skipped", missed: "Missed", snoozed: "Snoozed" };
const STATUS_TINT = {
  taken: "bg-emerald-100 text-emerald-800",
  skipped: "bg-zinc-100 text-zinc-700",
  missed: "bg-rose-100 text-rose-800",
  snoozed: "bg-amber-100 text-amber-800",
};

function fmt(ts) {
  try { return new Date(ts).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return ts; }
}

function fmtDate(ts) {
  try { return new Date(ts).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return ts; }
}

function StatusPill({ status }) {
  if (!status || status === "active") return null;
  const tint = status === "discontinued" ? "bg-slate-100 text-slate-700 border-slate-300"
             : status === "paused"       ? "bg-amber-100 text-amber-800 border-amber-300"
             : "bg-rose-100 text-rose-700 border-rose-300";
  const label = status === "discontinued" ? "DISCONTINUED"
              : status === "paused"       ? "PAUSED"
              : status.toUpperCase();
  return <span className={`text-[9px] font-900 tracking-wider px-1.5 py-0.5 border ${tint}`}>{label}</span>;
}

/* ---- Archive dialog ---- */
function ArchiveDialog({ med, onClose, onDone }) {
  const [status, setStatus] = useState("discontinued");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const REASONS = ["Side effects", "Course complete", "Doctor changed prescription", "No longer needed", "Ineffective", "Other"];
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/vault/health/medications/${med.id}/archive`, { status, reason });
      toast.success(status === "paused" ? "Medication paused" : "Medication archived");
      onDone(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not archive"); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-[9998] bg-zinc-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={onClose} data-testid="med-archive-dialog">
      <div className="bg-white border border-zinc-200 shadow-2xl w-full sm:max-w-md sm:rounded-lg" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-zinc-200 flex items-center gap-2">
          <Archive className="w-5 h-5 text-slate-600" />
          <h3 className="font-heading font-800 text-lg">Archive {med.name}</h3>
        </div>
        <div className="p-4 space-y-3">
          <div className="flex gap-2">
            <button onClick={() => setStatus("discontinued")} data-testid="archive-discontinue"
              className={`flex-1 px-3 py-2 text-sm font-700 border ${status === "discontinued" ? "bg-slate-800 text-white border-slate-800" : "border-zinc-300 text-zinc-700"}`}>
              Discontinued
            </button>
            <button onClick={() => setStatus("paused")} data-testid="archive-pause"
              className={`flex-1 px-3 py-2 text-sm font-700 border ${status === "paused" ? "bg-amber-600 text-white border-amber-600" : "border-zinc-300 text-zinc-700"}`}>
              Paused
            </button>
          </div>
          <label className="block text-sm">
            <span className="font-600">Reason <span className="font-400 text-muted-foreground">(optional)</span></span>
            <select value={REASONS.includes(reason) ? reason : ""} onChange={(e) => setReason(e.target.value)}
              className="mt-1 w-full border border-zinc-300 px-3 py-2 text-sm">
              <option value="">— Pick a reason (optional) —</option>
              {REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="archive-reason"
              placeholder="Or type your own reason…" maxLength={200}
              className="mt-2 w-full border border-zinc-300 px-3 py-2 text-sm" />
          </label>
          <p className="text-[11px] text-muted-foreground">Archived medications keep their history and can be restored any time. Use Delete to remove permanently.</p>
        </div>
        <div className="p-4 border-t border-zinc-200 flex items-center justify-end gap-2">
          <button onClick={onClose} disabled={busy} className="px-4 py-2 border border-zinc-300 text-sm">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="archive-save"
            className="px-4 py-2 bg-slate-800 text-white text-sm font-700 disabled:opacity-60 inline-flex items-center gap-1.5">
            <Archive className="w-4 h-4" /> {busy ? "Saving…" : "Archive"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---- Medication row ---- */
function MedRow({ med, archived, onEdit, onArchive, onRestore, onRemove, onLog, onAttach }) {
  const [openAttach, setOpenAttach] = useState(false);
  return (
    <div className="border-b border-zinc-100 last:border-b-0 px-4 py-3" data-testid={`med-row-${med.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-heading font-800 text-zinc-900 truncate flex items-center gap-2 flex-wrap">
            <span>{med.name}</span>
            {med.strength && <span className="text-xs font-mono text-zinc-500">{med.strength}</span>}
            <StatusPill status={med.status} />
          </div>
          <div className="text-xs text-muted-foreground">
            {med.brand && <span>{med.brand} · </span>}
            {med.frequency || "Frequency not set"}
            {med.times && med.times.length > 0 && <span> · {med.times.join(", ")}</span>}
          </div>
          {med.refills_remaining !== null && med.refills_remaining !== undefined && !archived && (
            <div className="text-[11px] text-zinc-500 mt-0.5">{med.refills_remaining} refills remaining</div>
          )}
          {archived && med.status_reason && (
            <div className="text-[11px] text-zinc-600 mt-0.5"><em>{med.status_reason}</em>{med.discontinued_at && ` · since ${fmtDate(med.discontinued_at)}`}</div>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={() => setOpenAttach((v) => !v)} data-testid={`med-attach-${med.id}`} title="Photos" className="p-1.5 text-zinc-400 hover:text-emerald-600"><Camera className="w-4 h-4" /></button>
          {!archived && <button onClick={() => onEdit(med)} data-testid={`med-edit-${med.id}`} title="Edit" className="p-1.5 text-zinc-400 hover:text-primary"><Edit2 className="w-4 h-4" /></button>}
          {archived ? (
            <button onClick={() => onRestore(med)} data-testid={`med-restore-${med.id}`} title="Restore" className="p-1.5 text-zinc-400 hover:text-emerald-600"><RotateCcw className="w-4 h-4" /></button>
          ) : (
            <button onClick={() => onArchive(med)} data-testid={`med-archive-${med.id}`} title="Archive" className="p-1.5 text-zinc-400 hover:text-slate-700"><Archive className="w-4 h-4" /></button>
          )}
          <button onClick={() => onRemove(med)} data-testid={`med-remove-${med.id}`} title="Delete permanently" className="p-1.5 text-zinc-400 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>
      {!archived && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          <button onClick={() => onLog(med, "taken")}  data-testid={`med-log-taken-${med.id}`}   className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-600 text-white text-xs font-700"><Check className="w-3 h-3" /> Taken</button>
          <button onClick={() => onLog(med, "skipped")} data-testid={`med-log-skipped-${med.id}`} className="inline-flex items-center gap-1 px-2.5 py-1 border border-zinc-300 text-zinc-700 text-xs font-700"><XCircle className="w-3 h-3" /> Skip</button>
          <button onClick={() => onLog(med, "missed")}  data-testid={`med-log-missed-${med.id}`}  className="inline-flex items-center gap-1 px-2.5 py-1 border border-rose-300 text-rose-700 text-xs font-700"><Clock className="w-3 h-3" /> Missed</button>
          <button onClick={() => onLog(med, "snoozed")} data-testid={`med-log-snoozed-${med.id}`} className="inline-flex items-center gap-1 px-2.5 py-1 border border-amber-300 text-amber-700 text-xs font-700"><Pause className="w-3 h-3" /> Snooze</button>
        </div>
      )}
      {openAttach && (
        <div className="mt-3">
          <AttachmentsPanel parentType="medication" parentId={med.id} defaultTags={["medication"]} compact />
        </div>
      )}
    </div>
  );
}

function HistoryPanel({ mid, name, onBack }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get(`/vault/health/medications/${mid}/events?limit=200`)
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, [mid]);

  return (
    <div data-testid="med-history-panel">
      <button onClick={onBack} data-testid="med-history-back" className="text-sm text-muted-foreground inline-flex items-center gap-1 hover:text-primary mb-3">
        <ChevronLeft className="w-4 h-4" /> Back
      </button>
      <h2 className="font-heading font-900 text-xl flex items-center gap-2 mb-3"><History className="w-5 h-5 text-emerald-600" /> {name} · History</h2>
      {items === null ? <Spinner /> : items.length === 0 ? (
        <p className="text-sm text-muted-foreground">No dose events yet. Use the Taken / Skip / Missed / Snooze buttons on the medication row to log events.</p>
      ) : (
        <div className="border border-zinc-200 bg-white">
          {items.map((e) => (
            <div key={e.id} className="px-4 py-2.5 border-b border-zinc-100 last:border-b-0 flex items-center justify-between text-sm">
              <span className={`text-xs font-700 px-2 py-0.5 ${STATUS_TINT[e.status]}`}>{STATUS_LABEL[e.status]}</span>
              <span className="text-xs text-muted-foreground font-mono">{fmt(e.actual_ts)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function MedicationsView({ onBack }) {
  const [items, setItems] = useState(null);
  const [archived, setArchived] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [archivingMed, setArchivingMed] = useState(null);
  const [historyOf, setHistoryOf] = useState(null);

  const load = async () => {
    try {
      const [a, arch] = await Promise.all([
        api.get("/vault/health/medications?status=active"),
        api.get("/vault/health/medications?status=archived"),
      ]);
      setItems(a.data.items || []);
      setArchived(arch.data.items || []);
    } catch { setItems([]); setArchived([]); }
  };

  useEffect(() => { load(); }, []);

  const remove = async (med) => {
    if (!window.confirm(`Permanently delete ${med.name}? Its dose history stays in your vault but the medication and its photos will be hidden.`)) return;
    try {
      await api.delete(`/vault/health/medications/${med.id}`);
      toast.success("Medication deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };

  const restore = async (med) => {
    try {
      await api.post(`/vault/health/medications/${med.id}/restore`);
      toast.success(`${med.name} restored`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not restore"); }
  };

  const logEvent = async (med, status) => {
    try {
      await api.post(`/vault/health/medications/${med.id}/events`, { status });
      const map = { taken: "Marked taken", skipped: "Marked skipped", missed: "Marked missed", snoozed: "Snoozed" };
      toast.success(map[status]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not log"); }
  };

  if (historyOf) return <HistoryPanel mid={historyOf.id} name={historyOf.name} onBack={() => setHistoryOf(null)} />;

  return (
    <div data-testid="medications-view">
      <div className="flex items-center justify-between mb-3">
        <button onClick={onBack} data-testid="medications-back" className="text-sm text-muted-foreground inline-flex items-center gap-1 hover:text-primary">
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        <button onClick={() => setAdding(true)} data-testid="medications-add"
          className="px-3 py-2 bg-emerald-600 text-white text-sm font-700 inline-flex items-center gap-1.5">
          <Plus className="w-4 h-4" /> Add medication
        </button>
      </div>

      <div className="bg-gradient-to-br from-emerald-50 to-white border border-zinc-200 p-4 mb-4">
        <div className="flex items-center gap-3">
          <Pill className="w-6 h-6 text-emerald-600" />
          <div>
            <h1 className="font-heading font-900 text-2xl">Medications</h1>
            <p className="text-xs text-muted-foreground">{items?.length || 0} active · {archived?.length || 0} archived · reminders coming soon</p>
          </div>
        </div>
      </div>

      {items === null ? <Spinner /> : items.length === 0 ? (
        <div className="border border-dashed border-zinc-300 p-8 text-center" data-testid="medications-empty">
          <Pill className="w-8 h-8 mx-auto text-zinc-300 mb-2" />
          <p className="text-sm text-muted-foreground mb-3">No medications yet. Tap <strong>Add medication</strong> — type 2 letters to search common medications.</p>
          <button onClick={() => setAdding(true)} className="px-4 py-2 bg-emerald-600 text-white text-sm font-700 inline-flex items-center gap-1.5">
            <Plus className="w-4 h-4" /> Add your first medication
          </button>
        </div>
      ) : (
        <div className="border border-zinc-200 bg-white">
          {items.map((m) => (
            <div key={m.id}>
              <MedRow med={m} archived={false} onEdit={setEditing} onArchive={setArchivingMed} onRestore={restore} onRemove={remove} onLog={logEvent} />
              <button onClick={() => setHistoryOf(m)} data-testid={`med-history-${m.id}`}
                className="text-[11px] text-emerald-700 font-700 px-4 pb-2 hover:underline">
                View history →
              </button>
            </div>
          ))}
        </div>
      )}

      {archived && archived.length > 0 && (
        <div className="mt-4 border border-zinc-200 bg-white" data-testid="archived-medications">
          <button onClick={() => setShowArchived((v) => !v)} data-testid="archived-toggle"
            className="w-full flex items-center justify-between px-4 py-3 text-sm font-700 text-zinc-700 hover:bg-zinc-50">
            <span className="inline-flex items-center gap-2"><Archive className="w-4 h-4 text-slate-500" /> Archived medications · {archived.length}</span>
            {showArchived ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {showArchived && (
            <div className="border-t border-zinc-100">
              {archived.map((m) => (
                <MedRow key={m.id} med={m} archived={true} onEdit={setEditing} onArchive={setArchivingMed} onRestore={restore} onRemove={remove} onLog={logEvent} />
              ))}
            </div>
          )}
        </div>
      )}

      {(adding || editing) && <MedicationDialog initial={editing} onClose={() => { setAdding(false); setEditing(null); }} onSaved={load} />}
      {archivingMed && <ArchiveDialog med={archivingMed} onClose={() => setArchivingMed(null)} onDone={load} />}
    </div>
  );
}
